import React from 'react';
import clsx from 'clsx';

import Link from '@dashboard/components/Link';
import { Thumbnail } from '@dashboard/components/OrderItemsCard';
import { OrderCard } from './OrderCard';

export const joinDetails = parts => parts.filter(Boolean).join(' · ');

/** One item row: image, name (linked when `href`), grey sub-line, amount flush right, extra lines underneath. */
export const ItemRow = ({ showImage, imageUrl, name, href, details, amount, sub, badge }) => (
  <li className="py-3 border-b border-gray-200 dark:border-border" data-testid="order-item">
    <div className="flex items-start gap-x-3">
      {showImage && <Thumbnail url={imageUrl} />}
      <div className="min-w-0 flex-1">
        <div className="font-semibold break-words">
          {href ? (
            <Link bare to={href} className="hover:text-gray-600 dark:hover:text-muted-foreground">
              {name}
            </Link>
          ) : (
            name
          )}
          {badge ? <span className="ml-2 align-middle">{badge}</span> : null}
        </div>
        {details ? (
          <div className="text-sm text-gray-500 dark:text-muted-foreground">{details}</div>
        ) : null}
      </div>
      {amount != null && <div className="whitespace-nowrap text-right">{amount}</div>}
    </div>
    {(Array.isArray(sub) ? sub : sub ? [sub] : []).map((line, index) => (
      // Lines under a product (setup fee, its discount, fees, total) share the right edge with the row above.
      <div
        key={index}
        className="flex items-start gap-x-3 text-sm text-gray-500 dark:text-muted-foreground"
      >
        {showImage && <div className="w-10 shrink-0" />}
        <div className="min-w-0 flex-1">{line.label}</div>
        <div className="whitespace-nowrap text-right">{line.amount}</div>
      </div>
    ))}
  </li>
);

export const ItemGroup = ({ title, count, action, children }) => (
  <>
    <div className="flex items-center justify-between font-semibold mt-3 first:mt-0">
      <span>
        {title}
        {count ? (
          <span className="ml-1.5 font-normal text-gray-500 dark:text-muted-foreground">
            {count}
          </span>
        ) : null}
      </span>
      {action ? <span className="font-normal">{action}</span> : null}
    </div>
    <ul>{children}</ul>
  </>
);

/** Totals rows: { label, value, bold?, rule? }. Spacing above each row only; the rule before the total gets both. */
export const TotalsList = ({ totals }) => (
  <dl data-testid="order-totals">
    {totals.map((row, index) => (
      <div
        key={row.key ?? index}
        className={clsx('flex justify-between gap-x-4 pt-3 text-sm', {
          'mt-3 border-t border-gray-200 dark:border-border': row.rule,
          'font-semibold text-gray-900 dark:text-foreground': row.bold,
          'text-gray-500 dark:text-muted-foreground': !row.bold,
        })}
      >
        <dt>{row.label}</dt>
        <dd className="whitespace-nowrap tabular-nums">{row.value}</dd>
      </div>
    ))}
  </dl>
);

/** The grey, invoice-like block headed "Summary" (Andy, 3 Oct 2026): item groups first, then the totals. */
export const OrderPaymentBlock = ({ title = 'Summary', children }) => (
  <OrderCard title={title}>
    <div
      className="rounded-lg bg-gray-50 dark:bg-card px-4 py-3 mt-3"
      data-testid="order-payment-block"
    >
      {children}
    </div>
  </OrderCard>
);
